import React from "react";
import { cn } from "@/lib/utils";
import { motion, useAnimation } from "framer-motion";
import { useDefaultDeck } from "@/hooks/use-default-deck";
import { useOpponentDeck } from "@/hooks/use-opponent-deck";
import { useLocalDecks } from "@/hooks/use-local-decks";
import { cardImageCache } from "@/services/card-image-cache";
import { useResponsive } from "@/lib/responsive";
import { USE_LOCAL_ASSETS } from "@/config/constants";

interface CardProps {
  value: string;
  onClick?: () => void;
  onDragPlay?: () => void;
  disabled?: boolean;
  selected?: boolean;
  className?: string;
  noAnimation?: boolean;
  draggable?: boolean;
  isOpponent?: boolean;
  opponentId?: number;
}

const typeMap = {
  A: "♠️",
  B: "♥️",
  C: "♣️",
  D: "♦️",
} as const;

type CardType = keyof typeof typeMap;

const numberMap: Record<string, string> = {
  "11": "J",
  "12": "Q",
  "13": "K",
  "14": "A",
};

export function Card({
  value,
  onClick,
  onDragPlay,
  disabled,
  selected,
  className,
  noAnimation = false,
  draggable = false,
  isOpponent = false,
  opponentId,
}: CardProps) {
  if (!value) {
    console.error("Invalid card value:", value);
    return null;
  }

  const controls = useAnimation();
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragSuccess, setDragSuccess] = React.useState(false);
  
  // Use our enhanced responsive utility with combined width/height detection
  const { 
    getCardSize, 
    dimensions, 
    isLandscape, 
    isPortrait, 
    screenSize,
    widthSize,
    heightSize,
    isMobile,
    isTablet 
  } = useResponsive();

  const systemDeckUrl = 'https://teacherwilliam.sgp1.cdn.digitaloceanspaces.com/spar/deck/free/default';
  
  // Get local decks utility for asset path generation
  const { getCardImagePath } = useLocalDecks();
  
  // Get the current player's default deck
  const { data: defaultDeckData } = useDefaultDeck();
  
  // Get the opponent's default deck if this is an opponent card
  const { data: opponentDeckData } = useOpponentDeck(isOpponent ? opponentId || null : null);
  
  // Determine which deck URL to use based on whether this is an opponent card
  let deckUrl: string;
  if (isOpponent && opponentDeckData?.deckUrl) {
    // Use opponent's deck
    deckUrl = opponentDeckData.deckUrl;
  } else {
    // Use current player's deck
    deckUrl = defaultDeckData?.deckUrl || systemDeckUrl;
  }
  
  // Generate the card image URL - use local assets if enabled
  const cardImageUrl = USE_LOCAL_ASSETS 
    ? getCardImagePath(deckUrl, value)
    : (value === "back" ? `${deckUrl}/back.svg` : `${deckUrl}/${value}.svg`);

  // Special handling for "back" value to show card back
  const isCardBack = value === "back";

  // Only parse suit/number for regular cards (not backs)
  let type: CardType | undefined;
  let number: string | undefined;
  let displayNumber: string | undefined;
  let suit: string | undefined;
  let isRed: boolean = false;

  if (!isCardBack && value.length >= 2) {
    type = value.charAt(0) as CardType;
    number = value.slice(1);
    displayNumber = numberMap[number] || number;
    suit = typeMap[type] || "?";
    isRed = type === "B" || type === "D";
  }
  
  // Preload this card image in the cache (skip if using local assets)
  React.useEffect(() => {
    // Skip preloading when using local assets
    if (USE_LOCAL_ASSETS) {
      return;
    }
    
    // Preload the image if it's not in cache already
    if (deckUrl) {
      cardImageCache.loadImage(cardImageUrl);
    }
  }, [cardImageUrl, deckUrl]);

  // Reset position after playing the card
  React.useEffect(() => {
    if (dragSuccess) {
      const timeout = setTimeout(() => {
        setDragSuccess(false);
        controls.start({ x: 0, y: 0, scale: 1, opacity: 1 });
      }, 300);
      
      return () => clearTimeout(timeout);
    }
  }, [dragSuccess, controls]);

  // Simplified tap handling - first tap selects, second tap plays
  const handleTap = () => {
    if (disabled || !draggable || !onDragPlay) return;
    
    if (selected) {
      // Card is already selected, so play it
      setDragSuccess(true);
      controls.start({ 
        opacity: 0,
        transition: { 
          opacity: { duration: 0.15, ease: "easeOut" }
        } 
      });
      onDragPlay();
    } else {
      // First tap just selects the card
      onClick?.();
    }
  };

  // Get responsive animation values based on screen dimensions
  const getResponsiveAnimationValues = () => {
    // Calculate hover lift amount
    let hoverLiftY = -6; // default
    if (isPortrait) {
      hoverLiftY = isMobile ? -4 : -6;
    } else {
      hoverLiftY = isMobile ? -6 : -8;
    }

    // Calculate hover scale amount
    let hoverScale = 1.05; // default
    if (isPortrait) {
      hoverScale = isMobile ? 1.03 : 1.04;
    }

    // Calculate tap scale amount
    let tapScale = isMobile ? 0.97 : 0.95;

    // Spring animation values - no rotation
    let springStiffness = isLandscape ? 400 : 350;
    let springDamping = isMobile ? 20 : 25;

    return {
      hoverLiftY,
      hoverScale,
      tapScale,
      springStiffness,
      springDamping
    };
  };

  // Get responsive values
  const { 
    hoverLiftY, 
    hoverScale, 
    tapScale, 
    springStiffness, 
    springDamping 
  } = getResponsiveAnimationValues();

  return (
    <motion.button
      onClick={!draggable ? onClick : handleTap}
      disabled={disabled || isCardBack}
      drag={false}
      // Responsive hover animation based on screen size and orientation - no rotation
      whileHover={!disabled && !noAnimation && !isCardBack ? { 
        y: hoverLiftY,
        scale: hoverScale,
        rotate: 0 // Explicitly set rotation to 0
      } : {}}
      // Responsive tap animation based on screen size - no rotation
      whileTap={!disabled && !noAnimation && !isCardBack ? { 
        scale: tapScale,
        rotate: 0 // Explicitly set rotation to 0
      } : {}}
      animate={controls}
      initial={{ x: 0, y: 0, scale: 1, opacity: 1, rotate: 0 }} // Explicitly set initial rotation to 0
      transition={{
        type: "spring",
        stiffness: springStiffness,
        damping: springDamping,
      }}
      data-card={value}
      className={cn(
        "card relative rounded-lg overflow-hidden transform-gpu",
        // Apply default responsive sizes based on screen dimensions if no className provided
        !className && (isPortrait
          ? (isMobile 
              ? "w-[3.5rem] h-[5rem]" 
              : isTablet 
                ? "w-[4rem] h-[6rem]" 
                : "w-[5rem] h-[7.5rem]")
          : (isMobile 
              ? "w-[4rem] h-[6rem]" 
              : isTablet 
                ? "w-[5rem] h-[7rem]" 
                : "w-[6rem] h-[8.5rem]")
        ),
        // Add responsive shadow based on device size
        !disabled && !isCardBack && (
          isMobile 
            ? "shadow-sm" 
            : "shadow-md"
        ),
        // Rest of the conditional classes
        disabled && !isCardBack && "opacity-50 saturate-50 shadow-none",
        selected && (
          isPortrait 
            ? "ring-2 ring-red-500 shadow-md shadow-red-500/30" 
            : "ring-4 ring-red-500 shadow-xl shadow-red-500/40"
        ),
        !disabled && !selected && !isCardBack && "hover:shadow-2xl hover:border-primary hover:shadow-primary/20",
        selected && (
          isPortrait
            ? "after:absolute after:inset-0 after:rounded-lg after:ring-4 after:ring-red-500/30"
            : "after:absolute after:inset-0 after:rounded-lg after:ring-8 after:ring-red-500/30"
        ),
        draggable && !disabled && !isCardBack && "cursor-pointer",
        isCardBack && "card-back",
        isOpponent && "opponent-card",
        className,
      )}
    >
      <div className="absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-white/10 to-transparent opacity-70 z-20 pointer-events-none"></div>
      <motion.div
        initial={{ rotate: 0 }} // Explicitly set initial rotation to 0
        // Responsive selection scale based on device size - no rotation
        animate={selected && !noAnimation ? 
          { scale: isPortrait ? (isMobile ? 1.05 : 1.06) : 1.08, rotate: 0 } : 
          { scale: 1, rotate: 0 }
        }
        transition={{ 
          duration: isMobile ? 0.25 : 0.3,
          ease: "easeOut"
        }}
        className="w-full h-full relative z-0"
        style={{ transform: 'rotate(0deg)' }} // Additional safeguard against rotation
      >
        <img
          src={cardImageUrl}
          alt={isCardBack ? "Card back" : `${value} card`}
          className={cn(
            "w-full h-full object-contain relative z-0",
            // Add responsive loading optimization based on device
            isMobile ? "loading-priority-high" : ""
          )}
          draggable="false"
          onLoad={(e) => {
            // Add subtle fade-in effect when card image loads
            const img = e.currentTarget;
            img.style.opacity = "0";
            img.style.transition = "opacity 0.2s ease-in-out";
            setTimeout(() => {
              img.style.opacity = "1";
            }, 10);
          }}
        />
      </motion.div>
    </motion.button>
  );
}
